import Image from 'next/image';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';

const COHORT_JOURNEY_STEP_IDS = ['kickoff', 'expertSessions', 'projectSubmissions', 'liveEvents', 'review'] as const;

const DEFAULT_DIAGRAM_SRC = '/images/enterprise/cohorts-journey-diagram.png';

/** Natural dimensions of `cohorts-journey-diagram.png` (for `next/image` layout). */
const DIAGRAM_WIDTH = 1070;
const DIAGRAM_HEIGHT = 700;

export type CohortJourneyStepId = (typeof COHORT_JOURNEY_STEP_IDS)[number];

export type CohortJourneyStepConfig = {
  id: CohortJourneyStepId;
};

export type CohortJourneyConfig = {
  diagramImage?: {
    src: string;
  };
  steps: CohortJourneyStepConfig[];
};

function buildCohortJourneyImageAlt(slug: string, stepIds: CohortJourneyStepId[], t: TFunction): string {
  const stepPhrases = stepIds.map((id) => {
    const raw = t(`enterprise.${slug}.cohortJourney.steps.${id}.label`) as string;
    return raw.replace(/\s*\n\s*/g, ' ').trim();
  });
  return `Cohort learning journey diagram showing the steps ${stepPhrases.join(', ')}.`;
}

function JourneyDiagramImage({ alt, src }: { alt: string; src: string }) {
  return (
    <Box
      flex='1'
      maxW={{ base: '100%', md: '535px' }}
      minW={{ base: 0, md: '240px' }}
      position='relative'
      sx={{ aspectRatio: `${DIAGRAM_WIDTH} / ${DIAGRAM_HEIGHT}` }}
      w='100%'
    >
      <Image
        alt={alt}
        quality={90}
        sizes='(max-width: 768px) 100vw, 535px'
        src={src}
        style={{ objectFit: 'contain' }}
        fill
      />
    </Box>
  );
}

export function CohortJourneySection({ config, slug }: { config: CohortJourneyConfig; slug: string }) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}.cohortJourney`;
  const stepIds = config.steps
    .map((s) => s.id)
    .filter((id): id is CohortJourneyStepId => (COHORT_JOURNEY_STEP_IDS as readonly string[]).includes(id));

  if (stepIds.length === 0) {
    return null;
  }

  const diagramSrc = config.diagramImage?.src ?? DEFAULT_DIAGRAM_SRC;
  const imageAlt = buildCohortJourneyImageAlt(slug, stepIds, t);

  return (
    <Box
      as='section'
      bg='white'
      pb={{
        base: '56px',
        md: '64px',
      }}
    >
      <ContentContainer contentMaxWidth='content-max-width-md' display='flex' flexDirection='column' gap='16px'>
        <Box
          bgGradient='linear(to-r, #002989, #0a0c11)'
          height={{ base: 'auto', md: '508px' }}
          overflow='hidden'
          position='relative'
          borderRadius={{
            md: '8px',
          }}
          mx={{
            base: '-16px',
            md: 'unset',
          }}
          padding={{
            base: '24px 16px',
            md: '75px 68px',
          }}
        >
          <Flex
            align={{ base: 'stretch', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            justify='space-between'
            gap={{
              base: 8,
              md: 10,
            }}
          >
            <Flex
              color='white'
              direction='column'
              flex={{ md: '0 1 363px' }}
              gap='18px'
              justifyContent='center'
              maxW={{ md: '363px' }}
            >
              <Heading
                as='h2'
                fontSize={{ base: '1.75rem', md: '2.25rem' }}
                fontWeight='600'
                letterSpacing='-1px'
                lineHeight='1.25'
              >
                {t(`${tPrefix}.title`)}
              </Heading>
              <Text fontSize='18px' lineHeight='1.5'>
                {t(`${tPrefix}.description`)}
              </Text>
            </Flex>

            <JourneyDiagramImage alt={imageAlt} src={diagramSrc} />
          </Flex>
        </Box>
      </ContentContainer>
    </Box>
  );
}
